import { cell, option, random, reject, shuffled } from './53-game-primitives.mjs';

const NAMES = ['Teal', 'Coral', 'Violet', 'Gold', 'Blue', 'Pink', 'Lime', 'Amber'];
const run = (tube) => {
  let n = 0;
  while (n < tube.length && tube[tube.length - n - 1] === tube.at(-1)) n++;
  return n;
};
const level = (state) => Math.max(1, Math.min(99, state.data?.stage || 1));

/** Difficulty increases only after an explicit solved-level advance. Colours
 * grow from3 to8; the spare-tube budget falls from2 to1. Concealment starts at
 * level7 and masks two, then three colours until their covering marble leaves. */
export function marbleDifficulty(stage) {
  const n = Math.max(1, Math.min(99, Math.trunc(stage) || 1));
  return {
    colours: Math.min(8, 3 + Math.floor((n - 1) / 2)),
    empty: n < 7 ? 2 : 1,
    hidden: n < 7 ? 0 : n < 11 ? 2 : 3,
    steps: Math.min(100, 18 + n * 5),
  };
}

/** Reverse exact legal maximal-run pours from solved tubes. The bounded witness
 * proves every seed solvable, including the one-spare expert layout. It is
 * returned only for tests and never placed in game state or visible metadata. */
export function progressiveMarblePuzzle(state, stage = level(state)) {
  const difficulty = marbleDifficulty(stage);
  const colours = shuffled(
    state,
    Array.from({ length: Math.max(6, difficulty.colours) }, (_, i) => i),
  ).slice(0, difficulty.colours);
  const tubes = colours
    .map((colour) => Array(4).fill(colour))
    .concat(Array.from({ length: difficulty.empty }, () => []));
  const solution = [];
  let previous = null;
  for (let step = 0; step < difficulty.steps; step++) {
    const choices = [];
    for (let from = 0; from < tubes.length; from++) {
      const source = tubes[from];
      if (!source.length) continue;
      for (let to = 0; to < tubes.length; to++) {
        const target = tubes[to];
        if (from === to || target.at(-1) === source.at(-1)) continue;
        if (previous?.from === to && previous?.to === from) continue;
        for (let count = 1; count <= Math.min(run(source), 4 - target.length); count++) {
          if (count === run(source) && source.length > count) continue;
          if (!target.length && count === source.length) continue;
          choices.push({ from, to, count });
        }
      }
    }
    if (!choices.length) break;
    const choice = choices[random(state, choices.length)];
    tubes[choice.to].push(...tubes[choice.from].splice(-choice.count));
    solution.unshift({ from: choice.to, to: choice.from, count: choice.count });
    previous = choice;
  }
  const concealed = difficulty.hidden ? colours.slice(-difficulty.hidden) : [];
  const identities = tubes.map((tube, t) => tube.map((_, i) => `${t}:${i}`));
  const moved = new Set();
  for (const step of solution) {
    const ids = identities[step.from].splice(-step.count);
    ids.forEach((id) => moved.add(id));
    identities[step.to].push(...ids);
  }
  return {
    tubes,
    solution,
    difficulty,
    revealed: tubes.map((tube, t) =>
      tube.map(
        (colour, index) =>
          index === tube.length - 1 || !concealed.includes(colour) || !moved.has(`${t}:${index}`),
      ),
    ),
  };
}

/** Initialize a bounded deterministic puzzle; caller owns immutable cloning and
 * replay validation. Neither presentation timing nor geometry enters policy. */
export function createProgressiveIdle(state, stage = 1) {
  if (!Number.isInteger(stage) || stage < 1 || stage > 99) reject('Invalid puzzle level.');
  if (state.gameId === 'ball-sort') {
    const puzzle = progressiveMarblePuzzle(state, stage);
    state.data = {
      tubes: puzzle.tubes,
      revealed: puzzle.revealed,
      capacity: 4,
      colours: puzzle.difficulty.colours,
      hidden: puzzle.difficulty.hidden,
      selected: -1,
      stage,
      stageComplete: false,
      transfers: 0,
      events: [],
    };
  } else if (state.gameId === 'pebble-balance') {
    const discs = Math.min(6, stage + 2);
    state.data = {
      pegs: [Array.from({ length: discs }, (_, i) => discs - i), [], []],
      discs,
      selected: -1,
      stage,
      stageComplete: false,
      transfers: 0,
      events: [],
    };
  } else reject('Unknown progressive puzzle.');
}

function sortMove(state, index) {
  const d = state.data;
  if (d.selected < 0) {
    if (!d.tubes[index]?.length) reject('Choose a tube with marbles.');
    d.selected = index;
    return;
  }
  if (index === d.selected) {
    d.selected = -1;
    return;
  }
  const sourceIndex = d.selected,
    source = d.tubes[sourceIndex],
    target = d.tubes[index];
  if (!target || target.length >= 4 || (target.length && target.at(-1) !== source.at(-1)))
    reject('Choose an empty tube or matching top colour.');
  // Stop before an unrevealed marble: its true colour must never influence a
  // visible batch length before the player uncovers it.
  let visibleRun = 0;
  for (
    let i = source.length - 1;
    i >= 0 && source[i] === source.at(-1) && d.revealed[sourceIndex][i];
    i--
  )
    visibleRun++;
  const count = Math.min(visibleRun, 4 - target.length);
  target.push(...source.splice(-count));
  d.revealed[sourceIndex].splice(-count);
  d.revealed[index].push(...Array(count).fill(true));
  if (source.length) d.revealed[sourceIndex][source.length - 1] = true;
  d.selected = -1;
  d.transfers++;
  d.stageComplete = d.tubes.every(
    (tube, t) =>
      !tube.length ||
      (tube.length === 4 && tube.every((v) => v === tube[0]) && d.revealed[t].every(Boolean)),
  );
  d.events = [{ type: 'transfer', from: sourceIndex, to: index, count, points: 0 }];
}
function hanoiMove(state, index) {
  const d = state.data;
  if (d.selected < 0) {
    if (!d.pegs[index]?.length) reject('Choose a tower with a disc.');
    d.selected = index;
    return;
  }
  if (d.selected === index) {
    d.selected = -1;
    return;
  }
  const sourceIndex = d.selected,
    source = d.pegs[sourceIndex],
    target = d.pegs[index];
  if (!target || (target.length && target.at(-1) < source.at(-1)))
    reject('A larger disc cannot rest on a smaller disc.');
  const disc = source.pop();
  target.push(disc);
  d.selected = -1;
  d.transfers++;
  d.stageComplete = d.pegs[2].length === d.discs;
  d.events = [{ type: 'transfer', from: sourceIndex, to: index, disc, points: 0 }];
}

/** Apply a legal selection/transfer or explicit held-result Next. A hidden
 * marble becomes permanently visible only when exposed; Undo restores the
 * prior immutable engine snapshot. No random reshuffle follows a transfer. */
export function moveProgressiveIdle(state, move) {
  const d = state.data;
  if (move.type === 'option' && move.index === 0 && d.stageComplete) {
    createProgressiveIdle(state, Math.min(99, d.stage + 1));
    // Idle levels are separate private puzzles; a solved explicit Next renews
    // its action budget without changing any score or scored replay policy.
    state.moves = 0;
    return;
  }
  if (d.stageComplete || move.type !== 'cell' || !Number.isInteger(move.index) || move.index < 0)
    reject('This puzzle is waiting for Next level.');
  d.events = [];
  if (state.gameId === 'ball-sort') sortMove(state, move.index);
  else hanoiMove(state, move.index);
  if (d.stageComplete) d.events.push({ type: 'complete', reason: 'level', points: 0 });
}

/** Find the shortest remaining three-peg route from public disc stacks. Each
 * state contains at most six discs (729 positions); illegal projections return
 * unavailable rather than suggesting a move. No state or selection is mutated. */
export function hanoiHint(view) {
  const stacks = view?.cells?.map((cell) => cell.discs);
  if (!stacks || stacks.length !== 3 || stacks.some((stack) => !Array.isArray(stack)))
    return { kind: 'unavailable' };
  const discs = stacks.flat();
  const total = discs.length;
  if (
    total < 2 ||
    total > 6 ||
    new Set(discs).size !== total ||
    discs.some((disc) => !Number.isInteger(disc) || disc < 1 || disc > total) ||
    stacks.some((stack) => stack.some((disc, i) => i > 0 && stack[i - 1] < disc))
  )
    return { kind: 'unavailable' };
  if (stacks[2].length === total) return { kind: 'complete' };
  const key = (pegs) => pegs.map((peg) => peg.join(',')).join('|');
  const queue = [{ pegs: stacks, first: null, distance: 0 }];
  const visited = new Set([key(stacks)]);
  for (let cursor = 0; cursor < queue.length && cursor < 729; cursor++) {
    const { pegs, first, distance } = queue[cursor];
    for (let from = 0; from < 3; from++) {
      const disc = pegs[from].at(-1);
      if (!disc) continue;
      for (let to = 0; to < 3; to++) {
        if (to === from || (pegs[to].length && pegs[to].at(-1) < disc)) continue;
        const next = pegs.map((peg) => [...peg]);
        next[to].push(next[from].pop());
        const id = key(next);
        if (visited.has(id)) continue;
        const step = first || { from, to };
        if (next[2].length === total) return { kind: 'move', ...step, remaining: distance + 1 };
        visited.add(id);
        queue.push({ pegs: next, first: step, distance: distance + 1 });
      }
    }
  }
  return { kind: 'unavailable' };
}

/** Project only revealed marble identities. Labels, layers, legal destinations
 * and scene contain no concealed answers. Towers expose size and legal targets
 * for keyboard/screen-reader parity; renderer owns materials and animation. */
export function progressiveIdleView(state) {
  const d = state.data,
    marble = state.gameId === 'ball-sort';
  const stacks = marble ? d.tubes : d.pegs;
  const cells = stacks.map((stack, index) => {
    const top = stacks[d.selected]?.at(-1);
    const valid =
      d.selected < 0
        ? !!stack.length
        : index === d.selected ||
          (marble
            ? stack.length < 4 && (!stack.length || stack.at(-1) === top)
            : !stack.length || stack.at(-1) > top);
    const visible = marble ? stack.map((v, i) => (d.revealed[index][i] ? v : null)) : [...stack];
    const description = marble
      ? visible.map((v) => (v === null ? 'concealed' : NAMES[v])).join(', ')
      : visible.join(', ');
    return cell(
      index,
      `${marble ? 'Tube' : 'Tower'} ${index + 1}: ${description || 'empty'}${marble ? `; ${4 - stack.length} spaces` : index === 2 ? '; goal tower' : ''}`,
      {
        ...(marble
          ? { layers: visible, capacity: 4, colour: stack.at(-1) }
          : { discs: visible, totalDiscs: d.discs }),
        selected: d.selected === index,
        destination: d.selected >= 0 && index !== d.selected && valid,
        disabled: !valid || d.stageComplete,
      },
    );
  });
  const current = marble
    ? stacks.filter(
        (s, t) => s.length === 4 && s.every((v) => v === s[0]) && d.revealed[t].every(Boolean),
      ).length
    : stacks[2].length;
  const total = marble ? d.colours : d.discs;
  const advanceLabel =
    !marble && d.discs === 6
      ? 'Practice again'
      : d.stage === 99
        ? 'Another expert puzzle'
        : 'Next level';
  const completionTitle =
    !marble && d.discs === 6
      ? 'Six-disc tower complete!'
      : d.stage === 99
        ? 'Expert puzzle complete!'
        : null;
  return {
    columns: marble ? Math.ceil(stacks.length / 2) : 3,
    cells,
    options: d.stageComplete ? [option(0, advanceLabel)] : [],
    advanceLabel,
    completionTitle,
    family: marble ? 'vessels' : 'hanoi',
    stage: d.stage,
    stageComplete: d.stageComplete,
    advanceAction: d.stageComplete ? { type: 'option', index: 0 } : null,
    goal: marble
      ? 'Sort every colour into its own full tube.'
      : 'Carry the complete tower to the glowing right platform.',
    status: d.stageComplete
      ? `${!marble && d.discs === 6 ? 'Six-disc practice complete' : d.stage === 99 ? 'Expert puzzle complete' : `Level ${d.stage} complete`} · ${d.transfers} ${d.transfers === 1 ? 'move' : 'moves'}`
      : d.selected >= 0
        ? 'Glowing destinations accept the lifted piece.'
        : marble
          ? `${d.colours} colours${d.revealed.some((tube) => tube.some((visible) => !visible)) ? ' · Grey marbles reveal when uncovered' : ''} · Choose a tube`
          : `${d.discs === 6 ? 'Six-disc practice' : `${d.discs} discs`} · Smaller always rests on larger · ${d.transfers} ${d.transfers === 1 ? 'move' : 'moves'}`,
    progress: {
      current,
      total,
      label: marble ? `${current} of ${total} colours sorted` : `${current} of ${total} discs home`,
    },
    scene: {
      selected: d.selected,
      capacity: 4,
      discs: d.discs,
      rows: marble || stacks.length > 5 ? 2 : 1,
      minimum: marble ? undefined : 2 ** d.discs - 1,
    },
  };
}

/** Find a proven continuation using only public layers, never hidden identities
 * or the generator witness. Breadth-first search expands at most4,000 boards;
 * exhaustion is inconclusive, not proof of a dead end. No state is mutated. */
export function marbleHint(view) {
  const tubes = view.cells.map((cell) => cell.layers);
  if (tubes.some((tube) => tube.some((colour) => colour === null))) return { kind: 'concealed' };
  const solved = (board) =>
    board.every((t) => !t.length || (t.length === 4 && t.every((c) => c === t[0])));
  if (solved(tubes)) return { kind: 'complete' };
  const key = (board) =>
    board
      .map((t) => t.join(''))
      .sort()
      .join('|');
  const queue = [{ tubes, first: null }],
    seen = new Set([key(tubes)]);
  for (let cursor = 0; cursor < queue.length && cursor < 4000; cursor++) {
    const current = queue[cursor];
    for (let from = 0; from < tubes.length; from++) {
      const source = current.tubes[from];
      if (!source.length) continue;
      const count = run(source);
      let emptyUsed = false;
      for (let to = 0; to < tubes.length; to++) {
        const target = current.tubes[to];
        if (
          to === from ||
          target.length === 4 ||
          (target.length && target.at(-1) !== source.at(-1))
        )
          continue;
        // Empty tubes are interchangeable; moving a whole uniform tube to one
        // merely renames it and cannot help solve the board.
        if (!target.length) {
          if (emptyUsed || count === source.length) continue;
          emptyUsed = true;
        }
        const next = current.tubes.map((t) => [...t]);
        next[to].push(...next[from].splice(-Math.min(count, 4 - target.length)));
        const first = current.first || { kind: 'move', from, to };
        if (solved(next)) return first;
        const identity = key(next);
        if (seen.has(identity)) continue;
        seen.add(identity);
        if (queue.length < 4000) queue.push({ tubes: next, first });
      }
    }
  }
  return { kind: queue.length >= 4000 ? 'limit' : 'stalled' };
}
